import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_BASE, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer } from "@phosphor-icons/react";
import { InlineQR } from "@/components/QRCodeButton";
import UpiPayQR from "@/components/UpiPayQR";
import { SupportingTendersView } from "@/components/SupportingTendersEditor";

/**
 * Public shareable page for a Payment-Pending (Temp) Invoice — mirrors
 * the sales-invoice public page so users get identical Print + UPI-QR UX.
 * Auto-fires window.print() when the URL has `?autoprint=1`.
 */
export default function PublicTempInvoice() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const autoprint = params.get("autoprint") === "1";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/public/temp-invoices/${id}?token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setError("This temp invoice link is invalid or has expired."));
  }, [id, token]);

  useEffect(() => {
    if (data && autoprint) {
      const t = setTimeout(() => window.print(), 350);
      return () => clearTimeout(t);
    }
  }, [data, autoprint]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;

  const t = data.temp;
  const company = data.company || {};
  const intra = (t.cgst_total || 0) + (t.sgst_total || 0) > 0;
  const balanceDue = Number(t.balance_amount || 0);

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="max-w-4xl mx-auto p-10 print:p-6">
        <div className="flex justify-end mb-6 print:hidden">
          <Button onClick={() => window.print()} className="h-10" variant="outline" data-testid="public-temp-print">
            <Printer size={14} className="mr-1.5" /> Print
          </Button>
        </div>

        <div className="border-2 border-black">
          <div className="p-6 border-b-2 border-black flex items-center justify-between gap-6">
            <img
              src={company.logo_url || "/brand/vetrich-logo.png"}
              alt={company.name || "Logo"}
              style={{ height: "96px", width: "auto", objectFit: "contain", flexShrink: 0 }}
              crossOrigin="anonymous"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Payment Pending Invoice</div>
              <h1 className="text-2xl font-bold tracking-tight mt-1" style={{ fontFamily: "'Chivo', sans-serif" }}>{company.name || "Vetrich"}</h1>
              <div className="text-[12px] text-gray-600 mt-1.5 whitespace-pre-line">{company.address}</div>
              {company.gstin && <div className="text-[12px] mt-1">GSTIN: <span className="font-mono">{company.gstin}</span> · State: {company.state} ({company.state_code})</div>}
              <div className="text-[12px]">Phone: {company.phone} · Email: {company.email}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Temp #</div>
              <div className="text-lg font-mono font-bold mt-1">{t.number}</div>
              <div className="mt-2 text-[12px]">Date: <span className="font-mono">{t.date}</span></div>
              <div className="mt-3 flex justify-end gap-3 items-start">
                {t.share_token && (
                  <InlineQR
                    url={`${window.location.origin}/p/temp-invoice/${t.id}?t=${t.share_token}`}
                    size={68}
                    caption="Scan"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-2 gap-8 border-b-2 border-black">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Bill To</div>
              <div className="font-bold text-base" data-testid="bill-to-name">{t.customer_name}</div>
              {t.customer_phone && <div className="text-[12px] mt-1" data-testid="bill-to-phone">Mobile: <span className="font-mono">{t.customer_phone}</span></div>}
              {t.customer_address && <div className="text-[12px] mt-0.5 whitespace-pre-line" data-testid="bill-to-address">{t.customer_address}</div>}
              {t.customer_gstin && <div className="text-[12px] mt-1">GSTIN: <span className="font-mono">{t.customer_gstin}</span></div>}
              {t.customer_state_code && <div className="text-[12px]">State Code: <span className="font-mono">{t.customer_state_code}</span></div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Payment Status</div>
              <div className="text-[13px]">Status: <span className="font-semibold uppercase">{t.status}</span></div>
              <div className="text-[13px]">Received: <span className="font-mono">{inr(t.received_amount)}</span></div>
              {(t.receipts || []).length > 0 && (() => {
                const uniqueModes = Array.from(new Set(
                  (t.receipts || []).map((r) => (r.mode || "").trim().toUpperCase().replace(/_/g, " "))
                )).filter(Boolean);
                if (!uniqueModes.length) return null;
                return (
                  <>
                    <div className="text-[13px]">
                      Modes: <span className="font-semibold font-mono">{uniqueModes.join(" · ")}</span>
                    </div>
                    <div className="mt-1 border border-gray-300 rounded">
                      <table className="w-full text-[11.5px]">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left px-2 py-1 font-semibold">Mode</th>
                            <th className="text-right px-2 py-1 font-semibold">Amount</th>
                            <th className="text-left px-2 py-1 font-semibold">UPI ID / Transaction ID / UTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(t.receipts || []).map((r, i) => (
                            <tr key={i} className="border-t border-gray-200">
                              <td className="px-2 py-1 uppercase">{String(r.mode || "").replace(/_/g, " ")}</td>
                              <td className="px-2 py-1 font-mono text-right">{inr(r.amount || 0)}</td>
                              <td className="px-2 py-1 font-mono text-gray-600">{r.reference || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
              <div className="text-[13px]">Balance Due: <span className="font-mono font-semibold">{inr(t.balance_amount)}</span></div>
            </div>
          </div>

          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b-2 border-black" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">#</th>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">Description</th>
                {intra && <th className="text-left p-3 uppercase text-[10px] tracking-widest">HSN</th>}
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Qty</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Rate</th>
                {intra && <th className="text-right p-3 uppercase text-[10px] tracking-widest">Tax</th>}
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Total</th>
              </tr>
            </thead>
            <tbody>
              {(t.items || []).map((it, i) => (
                <tr key={it.id || it.product_id || `${it.hsn}-${it.name}-${i}`} className="border-b border-gray-300">
                  <td className="p-3 font-mono">{i + 1}</td>
                  <td className="p-3">{it.name}</td>
                  {intra && <td className="p-3 font-mono">{it.hsn}</td>}
                  <td className="p-3 text-right font-mono">{it.quantity} {it.unit || ""}</td>
                  <td className="p-3 text-right font-mono">{inr(it.rate)}</td>
                  {intra && <td className="p-3 text-right font-mono">{it.tax_rate}%</td>}
                  <td className="p-3 text-right font-mono font-semibold">{inr(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t-2 border-black flex">
            <div className="flex-1 p-6 text-[12px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Notes / Work Name</div>
              <div className="whitespace-pre-line">{t.notes || "—"}</div>
            </div>
            <div className="w-72 border-l-2 border-black p-6 text-[13px] space-y-1.5">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-mono">{inr(t.subtotal)}</span></div>
              {intra ? (
                <>
                  <div className="flex justify-between"><span className="text-gray-600">CGST</span><span className="font-mono">{inr(t.cgst_total)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">SGST</span><span className="font-mono">{inr(t.sgst_total)}</span></div>
                </>
              ) : (t.igst_total || 0) > 0 ? (
                <div className="flex justify-between"><span className="text-gray-600">IGST</span><span className="font-mono">{inr(t.igst_total)}</span></div>
              ) : null}
              <div className="border-t-2 border-black pt-2 mt-2 flex justify-between font-bold text-base" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <span>Grand Total</span><span className="font-mono">{inr(t.grand_total)}</span>
              </div>
              <div className="flex justify-between text-emerald-700"><span>Received</span><span className="font-mono">{inr(t.received_amount)}</span></div>
              <div className="flex justify-between font-semibold" style={{ color: balanceDue > 0 ? "#b91c1c" : "#137333" }}>
                <span>Balance</span><span className="font-mono">{inr(balanceDue)}</span>
              </div>
            </div>
          </div>

          <div className="p-6 border-t-2 border-black">
            <SupportingTendersView items={t.supporting_tenders} />
          </div>

          <div className="p-6 border-t-2 border-black flex items-end justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              {balanceDue > 0 && company.upi_id && (
                <UpiPayQR
                  vpa={company.upi_id}
                  payeeName={company.name}
                  amount={balanceDue}
                  reference={t.number}
                  size={82}
                  caption="Pay UPI"
                />
              )}
              {balanceDue <= 0 && (
                <div className="border-2 border-emerald-600 rounded-md bg-emerald-50 p-2 text-center min-w-[82px]">
                  <div className="h-9 w-9 rounded-full bg-emerald-600 mx-auto flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-emerald-700 font-bold mt-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
                    Fully Paid
                  </div>
                </div>
              )}
              <div className="text-[11px] text-gray-600 self-end">This is a computer-generated document. E&amp;OE.</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Authorised Signatory</div>
              <div className="mt-8 text-[12px] font-semibold">For {company.name}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Powered by {company.name || "Vetrich Technologies"} · Smart Accounting. Smarter Business.
        </div>
      </div>
    </div>
  );
}
